import React, { useState } from 'react';
import { motion } from 'motion/react';
import { User, Mail, Phone, MapPin, Building, Save, X } from 'lucide-react';

const AccountSettings: React.FC = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: 'Γιάννης',
    lastName: 'Παπαδόπουλος',
    email: 'giannis@enexan.com',
    phone: '+30 210 123 4567',
    company: 'Enexan Energy Solutions',
    position: 'Μηχανικός Ενέργειας',
    location: 'Αθήνα, Ελλάδα',
    timezone: 'Europe/Athens',
    language: 'el',
  });

  const handleSave = () => {
    // Save logic here
    setIsEditing(false);
  };

  const handleCancel = () => {
    // Reset form data
    setIsEditing(false);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 p-3">
            <User className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Account Settings</h1>
            <p className="text-gray-600">Manage your account information and preferences</p>
          </div>
        </div>

        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="rounded-xl bg-blue-600 px-4 py-2 font-medium text-white transition-all hover:bg-blue-700"
          >
            Edit Profile
          </button>
        )}
      </div>

      {/* Personal Information */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>

        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">First Name</label>
            <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3">
              <User className="h-5 w-5 text-gray-400" />
              {isEditing ? (
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="flex-1 text-gray-900 outline-none"
                />
              ) : (
                <span className="text-gray-900">{formData.firstName}</span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Last Name</label>
            <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3">
              <User className="h-5 w-5 text-gray-400" />
              {isEditing ? (
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="flex-1 text-gray-900 outline-none"
                />
              ) : (
                <span className="text-gray-900">{formData.lastName}</span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Email Address</label>
            <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3">
              <Mail className="h-5 w-5 text-gray-400" />
              {isEditing ? (
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="flex-1 text-gray-900 outline-none"
                />
              ) : (
                <span className="text-gray-900">{formData.email}</span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Phone Number</label>
            <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3">
              <Phone className="h-5 w-5 text-gray-400" />
              {isEditing ? (
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="flex-1 text-gray-900 outline-none"
                />
              ) : (
                <span className="text-gray-900">{formData.phone}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Professional Information */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-gray-900">Professional Information</h3>

        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Company</label>
            <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3">
              <Building className="h-5 w-5 text-gray-400" />
              {isEditing ? (
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="flex-1 text-gray-900 outline-none"
                />
              ) : (
                <span className="text-gray-900">{formData.company}</span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Position</label>
            <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3">
              <User className="h-5 w-5 text-gray-400" />
              {isEditing ? (
                <input
                  type="text"
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  className="flex-1 text-gray-900 outline-none"
                />
              ) : (
                <span className="text-gray-900">{formData.position}</span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Location</label>
            <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3">
              <MapPin className="h-5 w-5 text-gray-400" />
              {isEditing ? (
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="flex-1 text-gray-900 outline-none"
                />
              ) : (
                <span className="text-gray-900">{formData.location}</span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Timezone</label>
            <select
              value={formData.timezone}
              onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
              disabled={!isEditing}
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-gray-900 focus:border-blue-300 focus:ring-2 focus:ring-blue-100 focus:outline-none disabled:bg-gray-50"
            >
              <option value="Europe/Athens">Europe/Athens (GMT+2)</option>
              <option value="Europe/London">Europe/London (GMT+0)</option>
              <option value="America/New_York">America/New_York (GMT-5)</option>
              <option value="Asia/Tokyo">Asia/Tokyo (GMT+9)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Save/Cancel Buttons */}
      {isEditing && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-end gap-3 pt-6"
        >
          <button
            onClick={handleCancel}
            className="flex items-center gap-2 rounded-xl bg-gray-200 px-6 py-3 font-medium text-gray-700 transition-all hover:bg-gray-300"
          >
            <X className="h-4 w-4" />
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3 font-medium text-white transition-all hover:bg-blue-700"
          >
            <Save className="h-4 w-4" />
            Save Changes
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default AccountSettings;
